import React from "react";
import {
  Box,
  Flex,
  Heading,
  Link,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import { signInUtm } from "@/features/landing/constants";
import { getDefaultPageByIdentity } from "@/pages";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Trans } from "react-i18next";
import LandingPageFeatureSummaryCards from "@/features/landing/components/FeatureSummaryCards";
import useOpenChannelTalk from "@/shared/hooks/useOpenChannelTalk";

interface Props {
  isSignedIn: boolean;
}

// 시안: 검정 배경 위에 우측 정렬한 가상 요소로 배경 이미지를 얹는다.
// 하단 72px은 #000으로 페이드시켜 다음 흰 섹션과의 경계를 없앤다.
const HERO_IMAGE_GRADIENT =
  "linear-gradient(to bottom, transparent calc(100% - 72px), #000000)";
const HERO_IMAGE_MASK = "linear-gradient(to right, transparent, black 18%)";

// 흰 pill CTA 공통 스타일. 오버레이 hover/active는 랜딩 전역에서 쓰는 패턴을 그대로 따른다.
const heroButtonStyle = {
  padding: {
    base: "12px 24px",
    lg: "16px 32px",
  },
  background: "#FFFFFF",
  color: "#2A2A2A",
  borderRadius: "full",
  fontSize: {
    base: "16px",
    lg: "18px",
  },
  lineHeight: "24px",
  fontWeight: 700,
  height: "auto",
  textAlign: "center" as const,
  position: "relative" as const,
  overflow: "hidden",
  _hover: {
    textDecoration: "none",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: "#2A2A2A",
      opacity: 0.08,
    },
  },
  _active: {
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: "#2A2A2A",
      opacity: 0.2,
    },
  },
};

function LandingPageHeroSection({ isSignedIn }: Props) {
  const identity = useIdentity();
  const openChannelTalk = useOpenChannelTalk();
  const { t, tSentence } = useI18nHelper();

  return (
    <Flex
      as={"section"}
      aria-labelledby={"landing-header-title"}
      flexDirection={"column"}
      background={"#000000"}
    >
      {/* 배경 이미지는 이 블록 높이까지만 깔린다. 아래 카드 영역은 순수 검정. */}
      <Box
        position={"relative"}
        overflow={"hidden"}
        paddingX={{
          base: "16px",
          xl: "320px",
        }}
        paddingTop={{
          base: "120px",
          lg: "160px",
        }}
        paddingBottom={{
          base: "60px",
          lg: "120px",
        }}
        _after={{
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          aspectRatio: "3 / 2",
          maxWidth: "100%",
          backgroundImage: `${HERO_IMAGE_GRADIENT}, url('/images/landing-hero-pathway.webp')`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "30% center",
          opacity: 0.7,
          pointerEvents: "none",
          "@media (min-width: 768px)": {
            backgroundPosition: "center",
            maskImage: HERO_IMAGE_MASK,
            WebkitMaskImage: HERO_IMAGE_MASK,
            opacity: 1,
          },
          "@media (min-width: 1440px)": {
            backgroundSize: "contain",
          },
        }}
      >
        <Flex
          position={"relative"}
          zIndex={1}
          flexDirection={"column"}
          maxWidth={"1318px"}
          margin={"0 auto"}
          width={"100%"}
          gap={{
            base: "24px",
            lg: "48px",
          }}
          alignItems={{
            base: "center",
            md: "flex-start",
          }}
        >
          <Flex
            flexDirection={"column"}
            gap={{
              base: "12px",
              lg: "20px",
            }}
            alignItems={{
              base: "center",
              md: "flex-start",
            }}
          >
            <Text
              color={"#FFFFFF"}
              fontSize={{
                base: "14px",
                lg: "16px",
              }}
              lineHeight={"24px"}
              fontWeight={500}
              letterSpacing={"0.06em"}
            >
              {t("landing:hero.eyebrow")}
            </Text>
            <VisuallyHidden>
              <Heading as="h1" id={"landing-header-title"}>
                {tSentence("landing:hero.title_h1")}
              </Heading>
            </VisuallyHidden>
            <Box
              aria-hidden={true}
              textAlign={{
                base: "center",
                md: "start",
              }}
              fontSize={{ base: "40px", lg: "80px" }}
              lineHeight={1.2}
              fontWeight={600}
              color={"#FFFFFF"}
            >
              <Trans
                i18nKey={"landing:hero.title_main"}
                components={{
                  br: <Box as={"span"} display={"block"} />,
                }}
              />
            </Box>
            <Text
              color={"#FFFFFF"}
              fontSize={{
                base: "16px",
                lg: "18px",
              }}
              lineHeight={{
                base: "24px",
                lg: "28px",
              }}
              textAlign={{
                base: "center",
                md: "start",
              }}
            >
              {tSentence("landing:hero.title_description")}
            </Text>
          </Flex>
          <Flex
            flexDirection={{
              base: "column",
              md: "row",
            }}
            justifyContent={"center"}
            gap={{
              base: "12px",
              lg: "16px",
            }}
          >
            <Link
              {...heroButtonStyle}
              href={isSignedIn ? getDefaultPageByIdentity(identity) : signInUtm}
            >
              {isSignedIn
                ? tSentence("landing:enter_service")
                : tSentence("landing:hero.cta_sign_in")}
            </Link>
            <Link {...heroButtonStyle} as={"button"} onClick={openChannelTalk}>
              {tSentence("landing:contact_sales")}
            </Link>
            {/* TODO(시안): 패스픽 진입 임시 버튼. 랜딩 개편 시 배치를 다시 정한다. */}
            {/* PATHPICK_LANDING_PATH 는 @/features/pathpick/features/landing/constants 에 있다. */}
            {/*<Link {...heroButtonStyle} href={PATHPICK_LANDING_PATH}>*/}
            {/*  {tSentence("landing:pathpick_detail")}*/}
            {/*</Link>*/}
          </Flex>
        </Flex>
      </Box>
      <Box
        paddingX={{
          base: "16px",
          xl: "320px",
        }}
        paddingBottom={{
          base: "60px",
          lg: "80px",
        }}
      >
        <Box maxWidth={"1318px"} margin={"0 auto"} width={"100%"}>
          <LandingPageFeatureSummaryCards />
        </Box>
      </Box>
    </Flex>
  );
}

export default LandingPageHeroSection;
